import React, { useState, useEffect } from "react";
import { Card, Row, Col, Typography, Spin } from "antd";
import { ReportingService } from "../../../../services/services";
import LineChartExpert from "./KPI/LineChartExpert";
import PieExpert from "./KPI/PieExpert";
import Counts from "./KPI/Counts";
import TechChart from "./KPI/TechChart";
import TopExperts from "./KPI/TopExperts";
import RecentInquiries from "./KPI/RecentInquiries";
import InquiriesChart from "./KPI/InquiriesChart";
import RecentSubscribers from "./KPI/RecentSubscribers";
import SubscribersChart from "./KPI/SubscribersChart";
import "./dashboard.css";

const { Text } = Typography;

const DashboardSection = () => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState();
  const [expertiseDist, setExpertiseDist] = useState({});
  const [creationTimeline, setCreationTimeline] = useState({});
  const [technologyFrequency, setTechnologyFrequency] = useState({});
  const [topExperts, setTopExperts] = useState([]);
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [recentSubscribers, setRecentSubscribers] = useState([]);
  const [inquiriesTimeline, setInquiriesTimeline] = useState([]);
  const [subscribersTimeline, setSubscribersTimeline] = useState([]);

  useEffect(() => {
    // Fetch all data here
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await ReportingService.getCounts();
        setCounts(res.data.counts);
        setExpertiseDist(res.data.expertiseDist);
        setCreationTimeline(res.data.creationTimeline);
        setTechnologyFrequency(res.data.techAndToolsFrequency);
        setTopExperts(res.data.topExperts);
        setRecentInquiries(res.data.recentInquiries);
        setRecentSubscribers(res.data.recentSubscribers);
        setInquiriesTimeline(res.data.inquiriesTimeline);
        setSubscribersTimeline(res.data.subscribersTimeline);
      } catch (error) {
        console.error("Error fetching the data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="admin-dashboard__loading">
        <Spin tip="Loading dashboard..." />
      </div>
    );
  }

  const highlightStats = [
    {
      label: "Expert Profiles",
      value: counts?.totalExperts ?? 0,
      detail: "Active talent records",
    },
    {
      label: "Inbound Inquiries",
      value: counts?.inquiriesTotal ?? 0,
      detail: "Contact requests captured",
    },
    {
      label: "Audience Reach",
      value: counts?.newsletterSubscribers ?? 0,
      detail: "Newsletter subscribers",
    },
  ];

  return (
    <div className="admin-dashboard">
      <section className="admin-dashboard__hero">
        <div className="admin-dashboard__hero-copy">
          <Text className="admin-dashboard__eyebrow">Live Reporting</Text>
          <Text className="admin-dashboard__hero-kicker">
            Growth, demand, and expert performance in one view.
          </Text>
          <Text className="admin-dashboard__hero-text">
            Track talent supply, inquiry flow, and subscriber momentum with a
            calmer layout, tighter spacing logic, and more readable data
            surfaces.
          </Text>
        </div>
        <div className="admin-dashboard__hero-stats">
          {highlightStats.map((stat) => (
            <div key={stat.label} className="admin-dashboard__hero-stat">
              <Text className="admin-dashboard__hero-stat-label">
                {stat.label}
              </Text>
              <span className="admin-dashboard__hero-stat-value">
                {stat.value}
              </span>
              <Text className="admin-dashboard__hero-stat-detail">
                {stat.detail}
              </Text>
            </div>
          ))}
        </div>
      </section>

      <Row gutter={[20, 20]}>
        <Counts counts={counts} />
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={10}>
          <Card bordered={false} className="admin-panel-card admin-panel-card--soft">
            <PieExpert expertiseDist={expertiseDist} />
          </Card>
        </Col>
        <Col xs={24} xl={14}>
          <Card bordered={false} className="admin-panel-card">
            <TechChart technologyFrequency={technologyFrequency} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={10}>
          <Card bordered={false} className="admin-panel-card">
            <TopExperts topExperts={topExperts} />
          </Card>
        </Col>
        <Col xs={24} xl={14}>
          <Card bordered={false} className="admin-panel-card admin-panel-card--accent">
            <LineChartExpert creationTimeline={creationTimeline} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={10}>
          <Card bordered={false} className="admin-panel-card">
            <RecentInquiries recentInquiries={recentInquiries} />
          </Card>
        </Col>
        <Col xs={24} xl={14}>
          <Card bordered={false} className="admin-panel-card">
            <InquiriesChart inquiriesTimeline={inquiriesTimeline} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={10}>
          <Card bordered={false} className="admin-panel-card">
            <RecentSubscribers recentSubscribers={recentSubscribers} />
          </Card>
        </Col>
        <Col xs={24} xl={14}>
          <Card bordered={false} className="admin-panel-card">
            <SubscribersChart subscribersTimeline={subscribersTimeline} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardSection;
